import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import {
  Grid,
  Input,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TablePaginationProps,
} from '@mui/material';

import { Table } from '../../../components/table/table';
import { HeadCell, Row, RowCell } from '../../../components/table/types';

import {
  useLazyGetMerchantDepositRequestsQuery,
  GetMerchantDepositRequestsDTO,
} from '../../../api/controllers/merchant';
import { useGetCoinsQuery } from '../../../api';
import { formatBlockchainAmountWithCurrency } from '../../../utils/string/string';
import { FilterButton, FilterField } from '../../../components/filter/types';
import { Filter } from '../../../components/filter/filter';
import { TablePagination } from '../../../components/TablePagination';
import { UTCToDateTime } from '../../../utils';
import { RouterPaths } from '../../../router/paths';

export const MerchantPayments: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { profileUid } = useParams<'profileUid'>();

  // api data
  const [
    getMerchantDepositRequests,
    { data: merchantDepositRequests, isLoading: isMerchantCoinConfigLoading, originalArgs },
  ] = useLazyGetMerchantDepositRequestsQuery();
  const { data: coins } = useGetCoinsQuery();

  // variables
  const [isSearching, setSearchingStatus] = useState(false);

  // pagination logic
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const handleChangePage: TablePaginationProps['onPageChange'] = (e, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage: TablePaginationProps['onRowsPerPageChange'] = (e) => {
    setPage(0);
    setRowsPerPage(parseInt(e.target.value, 10));
  };

  // filter logic
  const [filterValues, setFilterValues] = useState<Partial<
    GetMerchantDepositRequestsDTO['params']
  > | null>(null);
  const handleFilterValues = (
    e:
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<GetMerchantDepositRequestsDTO['params']['coin_code']>
  ) => {
    const name = e.target.name;
    const value = e.target.value;

    setFilterValues((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };
  const resetFilter = async () => {
    if (!originalArgs || !filterValues) return;

    try {
      filterValues &&
        (await getMerchantDepositRequests({
          ...originalArgs,
          params: {
            page: 1,
            page_size: 20,
          },
        }).unwrap());
      setFilterValues(null);
      setSearchingStatus(false);
      setPage(0);
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  const searchByFilter = async () => {
    if (!originalArgs || !filterValues) return;

    try {
      await getMerchantDepositRequests({
        ...originalArgs,
        params: {
          ...originalArgs.params,
          page: 1,

          ...filterValues,
        },
      }).unwrap();
      setSearchingStatus(true);
      setPage(0);
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  useEffect(() => {
    if (!profileUid) return;

    const args = isSearching
      ? {
          merchant_profile_uid: profileUid,
          params: {
            page: page + 1,
            page_size: rowsPerPage,
            ...filterValues,
          },
        }
      : { merchant_profile_uid: profileUid, params: { page: page + 1, page_size: rowsPerPage } };

    getMerchantDepositRequests(args);
  }, [page, rowsPerPage]);

  const filterFields: FilterField[] = [
    {
      name: 'coin_code',
      title: t('merchant-payments.filter.coin'),
      component: (
        <Select
          name="coin_code"
          value={filterValues?.coin_code ?? ''}
          onChange={(e) => handleFilterValues(e)}
          variant="standard"
          sx={{ width: '140px' }}
        >
          {coins?.coins.map((it) => (
            <MenuItem key={it.id} value={it.code}>
              {it.name}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      name: 'address',
      title: t('merchant-payments.filter.address'),
      component: (
        <Input
          name="address"
          value={filterValues?.address ?? ''}
          onChange={(e) => handleFilterValues(e)}
          fullWidth
        />
      ),
    },
    {
      name: 'external_id',
      title: t('merchant-payments.filter.external-id'),
      component: (
        <Input
          name="external_id"
          value={filterValues?.external_id ?? ''}
          onChange={(e) => handleFilterValues(e)}
          fullWidth
        />
      ),
    },
  ];
  const filterButtons: FilterButton[] = [
    {
      name: 'search',
      text: t('ui.filter.search'),
      disabled: filterValues === null,
      onClick: () => searchByFilter(),
    },
    {
      name: 'reset-filter',
      text: t('ui.filter.reset'),
      onClick: () => resetFilter(),
    },
  ];

  // table cells and rows
  const headCells: HeadCell[] = [
    { id: '1', label: t('merchant-payments.head-cells.uid') },
    { id: '2', label: t('merchant-payments.head-cells.blockchain') },
    { id: '2', label: t('merchant-payments.head-cells.external-id') },
    { id: '3', label: t('merchant-payments.head-cells.status') },
    { id: '4', label: t('merchant-payments.head-cells.request-amount') },
    { id: '5', label: t('merchant-payments.head-cells.exchange-rate') },
    { id: '6', label: t('merchant-payments.head-cells.deposit-amount') },
    { id: '7', label: t('merchant-payments.head-cells.paid-amount') },
    { id: '8', label: t('merchant-payments.head-cells.fee') },
    { id: '9', label: t('merchant-payments.head-cells.created-at') },
  ];

  const rows: Row[] | undefined = merchantDepositRequests?.deposit_requests?.map((it) => {
    const {
      uid,
      ext_id,
      status,
      blockchain,
      req_amount,
      exchange_rate,
      req_coin_code,
      coin_code,
      deposit_amount,
      fee,
      paid_amount,
      created_at,
    } = it;

    const cells: RowCell[] = [
      { name: 'uid', value: uid },
      {
        name: 'blockchain',
        value: t(`common.blockchain.${blockchain}`),
      },
      {
        name: 'ext_id',
        value: ext_id,
      },
      {
        name: 'status',
        value: status,
      },
      {
        name: 'req_amount',
        value: formatBlockchainAmountWithCurrency(req_amount, req_coin_code),
      },
      {
        name: 'exchange_rate',
        value: exchange_rate,
      },
      {
        name: 'deposit_amount',
        value: formatBlockchainAmountWithCurrency(deposit_amount, coin_code),
      },
      {
        name: 'paid_amount',
        value: paid_amount != null && formatBlockchainAmountWithCurrency(paid_amount, coin_code),
      },
      {
        name: 'fee',
        value: fee != null && formatBlockchainAmountWithCurrency(fee, coin_code),
      },
      {
        name: 'created_at',
        value: created_at,
        formatting: (value: string) => {
          return UTCToDateTime(value);
        },
      },
    ];

    return {
      cells,
      onClick: () => navigate(`${RouterPaths.Profiles}/${profileUid}/merchant-payments/${uid}`),
      hover: true,
      sx: { cursor: 'pointer' },
    };
  });

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Grid size={{ xs: 12 }}>
          <Filter filterFields={filterFields} filterButtons={filterButtons} />
        </Grid>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', marginBottom: '12px' }}>
          <Table headCells={headCells} rows={rows} />
          <TablePagination
            page={page}
            rowsPerPage={rowsPerPage}
            total={merchantDepositRequests?.total ?? -1}
            totalPages={merchantDepositRequests?.total_pages ?? 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            isLoading={isMerchantCoinConfigLoading}
          />
        </Paper>
      </Grid>
    </>
  );
};
